import {
    Component,
    Host,
    h,
    Prop,
    State,
    ComponentWillLoad,
    Element,
    Listen,
    Event,
    EventEmitter,
} from '@stencil/core';
import { E_SEW_BUTTON_SIZE, E_SEW_BUTTON_HIERARCHY } from '../../sew-button/sew-button.types';
import { E_SEW_SEARCH_FIELD_SIZE } from './sew-search-field.types';
import { interceptValueChange } from '../../../utils/utils';
import QrCodeReader from '../sew-qrcodereader/qrcodereader';
import { hasDensityMode } from '../../../utils/hasDensityMode';

@Component({
    tag: 'sew-search-field',
    styleUrl: 'sew-search-field.scss',
    shadow: true,
})
export class SewSearchField implements ComponentWillLoad {
    @Element() public el: HTMLSewSearchFieldElement;

    /**
     * Items to be displayed in the datalist.
     *
     * **html-attribute: items**
     * @example
     * <sew-search-field items='item1, item2, item3'>
     * </sew-search-field>
     */
    @Prop() items: string;

    /** TODO Size of the search field. */
    @Prop({ reflect: true }) size: E_SEW_SEARCH_FIELD_SIZE = E_SEW_SEARCH_FIELD_SIZE.Default;

    @Prop({ reflect: true }) inline = false;

    /** Marks the input as invalid. */
    @Prop({ reflect: true }) error = false;

    /**
     * Activates the QR code scanner for input
     */
    @Prop() qrcodescanner = false;

    /** Sets the styles to the sew-ws theme. Default is false. */
    @Prop() ws = false;

    @Prop() noFilter = false;

    /** Hides the search icon in the input field */
    @Prop({ reflect: true }) noSearchIcon = false;

    /** value of input field */
    @State() private value: string;

    @State() private activeListItem = -1;

    @Event() itemClicked: EventEmitter<string>;
    @Event() qrCodeFound: EventEmitter;
    @Event() inputReset: EventEmitter<boolean>;

    @Listen('keydown', {})
    keyHandling(event: KeyboardEvent): void {
        const itemsCount = this.itemsArray.length;
        if (itemsCount === 0) return;

        if (event.code === 'ArrowDown') {
            this.activeListItem++;

            if (this.activeListItem >= itemsCount) {
                this.activeListItem = 0;
            }
        } else if (event.code === 'ArrowUp') {
            this.activeListItem--;

            if (this.activeListItem < 0) {
                this.activeListItem = itemsCount - 1;
            }
        } else if (event.code === 'Enter') {
            if (this.activeListItem !== -1) {
                this.itemSelected(this.itemsArray[this.activeListItem]);
                this.activeListItem = -1;
                event.preventDefault();
            }
        }
    }

    private qrScanner: QrCodeReader = null;

    private handleQrCodeScanner = (): void => {
        if (this.qrScanner == null) {
            this.qrScanner = new QrCodeReader(this.el);
        }

        this.qrScanner.onQrCodeFound = (codeData: string) => {
            this.qrCodeFound.emit(codeData);
        };

        this.qrScanner.handleScanner();
    };

    private inputElement: HTMLInputElement;
    private itemsArray: string[];

    private updateDisabledAttributeAndValue = () => {
        this.el.toggleAttribute('disabled', this.inputElement.disabled);
        this.updateValueState();
    };

    private disabledObserver = new MutationObserver(this.updateDisabledAttributeAndValue);

    private updateValueState = () => {
        this.value = this.inputElement.value;
    };

    private itemSelected(item) {
        this.itemClicked.emit(item);
        this.inputElement.value = item;
        this.updateValueState();
        this.inputElement.dispatchEvent(new CustomEvent('input', { bubbles: true }));
    }

    private filterItemByInputValue = (item: string) => {
        if (this.noFilter) {
            return item;
        }

        return item.toLowerCase().indexOf(this.value.toLowerCase()) !== -1;
    };

    private highlightSearchedValue = item => {
        const itemParts = item.split(this.value);
        const result = [itemParts[0]];

        for (let x = 1; x < itemParts.length; x++) {
            result.push(<b>{this.value}</b>);
            result.push(itemParts[x]);
        }

        return <span>{result}</span>;
    };

    /**
     * Marks the host element as focusd.
     */
    private onFocus = (): void => {
        this.el.setAttribute('focus', 'true');
    };

    /**
     * Removes the focus attribute.
     */
    private onBlur = (): void => {
        this.el.removeAttribute('focus');
        this.activeListItem = -1;
    };

    componentWillRender(): void {
        this.itemsArray = this.items ? this.items.split(',') : [];
    }

    componentWillLoad(): void {
        this.inputElement = this.el.querySelector('input') as HTMLInputElement;
        this.disabledObserver.observe(this.inputElement, { attributeFilter: ['disabled', 'value'] });
        this.inputElement.addEventListener('input', this.updateValueState);
        this.inputElement.addEventListener('focus', this.onFocus);
        this.inputElement.addEventListener('blur', this.onBlur);

        // Watch if the value is changed via property
        interceptValueChange(this.inputElement, value => (this.value = value));

        // Checks if the inital value was set via attribute
        this.updateValueState();
    }

    private renderDatalist(items) {
        return (
            <ul class="datalist">
                {items.filter(this.filterItemByInputValue).map((item, index) => {
                    item = item.trim();
                    return (
                        <li
                            class={{
                                'unset': true,
                                'datalist-item': true,
                                'active': this.activeListItem === index,
                            }}
                            onMouseDown={() => this.itemSelected(item)}
                        >
                            {this.highlightSearchedValue(item)}
                        </li>
                    );
                })}
            </ul>
        );
    }

    private getButtonSize() {
        if (this.inline) {
            return E_SEW_BUTTON_SIZE.XXS;
        } else if (this.size === E_SEW_SEARCH_FIELD_SIZE.XS) {
            return E_SEW_BUTTON_SIZE.XXXS;
        } else {
            return E_SEW_BUTTON_SIZE.XS;
        }
    }

    render() {
        return (
            <Host class={{ 'dirty': !!this.value, 'sew-ws': this.ws, [hasDensityMode(this.el)]: true }}>
                {!this.noSearchIcon && (
                    <sew-icon class="icon-search" icon="search" size={this.inline ? 's' : 'm'}></sew-icon>
                )}
                <slot></slot>
                {this.value && !this.ws && (
                    <sew-button
                        class="button-reset"
                        hierarchy={E_SEW_BUTTON_HIERARCHY.TERTIARY}
                        size={this.getButtonSize()}
                        onClick={this.resetValue}
                        icon="close"
                        showIconOnly={true}
                        suppress-density-mode
                    >
                        <button disabled={this.inputElement.disabled}></button>
                    </sew-button>
                )}
                {this.qrcodescanner &&
                    !this.error &&
                    /* Dient dazu den QrCode Scanner nur für Mobilgeräte zu aktivieren */
                    window.matchMedia(`(min-width: 0px) and (max-width: 991px)`).matches && (
                        <div>
                            <sew-icon
                                id="qrcode"
                                icon="qr-code-scanner"
                                onClick={this.handleQrCodeScanner}
                            ></sew-icon>
                            <div id="qr-dialog">
                                <a onClick={this.handleQrCodeScanner} id="qr-cancel">
                                    Cancel
                                </a>
                                <video id="qr-video" autoPlay></video>
                                <canvas id="qr-canvas"></canvas>
                            </div>
                        </div>
                    )}
                {this.value && this.ws && (
                    <button class="ws-button-reset" onClick={this.resetValue}>
                        <sew-icon icon="close" class="svg"></sew-icon>
                    </button>
                )}
                {this.itemsArray &&
                    this.value &&
                    this.itemsArray.indexOf(this.value) === -1 &&
                    this.renderDatalist(this.itemsArray)}
            </Host>
        );
    }

    private resetValue = () => {
        this.inputElement.value = '';
        this.inputReset.emit(true);
        this.inputElement.dispatchEvent(new CustomEvent('input', { bubbles: true }));
        this.updateValueState();
    };
}
